import type { Dispatch, ReactElement, SetStateAction } from "react"
import React, { Fragment, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery, useIsMutating, useMutation, useQuery, useQueryClient } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import * as Yup from "yup"
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward"
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { Avatar, Card, CardContent, CardHeader, Grid, IconButton, Skeleton, SvgIcon } from "@mui/material"
import {
  Button,
  InputText,
  Menu,
  MenuItem,
  NotificationUtils,
  Tooltip,
  Typography,
} from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { FormikProvider, useFormik } from "formik"
import { t } from "i18next"
import { capitalize } from "lodash"
import moment from "moment"
import { v4 as uuidv4 } from "uuid"

import { KonanAvatar } from "../../../components/core/Avatar"
import { BaseSimpleDialog } from "../../../components/dialogs/BaseSimpleDialog"
import { VersioningDialog } from "../../../components/dialogs/VersioningDialog"
import { VersionTag } from "../../../components/status/VersionTag"
import { KonanEmptyState } from "../../../components/utility/KonanEmptyState"
import { getTheme } from "../../../hooks/UseTheme"
import { KonanAPI } from "../../../services/KonanAPI"
import type { RootState } from "../../../store/ReduxStore"
import type { CreateWorkflowRequest } from "../../../types/custom/projects"
import { Operators } from "../../../types/custom/projects"
import type {
  CloneDataFilesRequest,
  CreateRulesetRequest,
  Rule,
  UpdateRulesetRequest,
  VersionChangeRequest,
} from "../../../types/custom/rules"
import type { ConditionListFile } from "../../../types/generated/api/ConditionListFile"
import type { DataFileCloneResponse } from "../../../types/generated/api/DataFileCloneResponse"
import type { Label } from "../../../types/generated/api/Label"
import type { PaginatedRuleSetVersionsListList } from "../../../types/generated/api/PaginatedRuleSetVersionsListList"
import type { RuleSetGroupRetrieve } from "../../../types/generated/api/RuleSetGroupRetrieve"
import type { RuleSetList } from "../../../types/generated/api/RuleSetList"
import type { RuleSetVersionRetrieve } from "../../../types/generated/api/RuleSetVersionRetrieve"
import type { RuleSetVersionsList } from "../../../types/generated/api/RuleSetVersionsList"
import { WorkflowCreateRequest } from "../../../types/generated/api/WorkflowCreateRequest"
import { WorkflowSchemaFeatureRequest } from "../../../types/generated/api/WorkflowSchemaFeatureRequest"
import { useIsPermitted } from "../../../utils/PermissionsHelpers"
import { formatWorkflowComponent } from "../../../utils/WorkflowBlocksHelpers"
import { levelSchema } from "../../../utils/conditionHelpers"
import { PROHIBITED_REASONS_KEYWORDS } from "../../../utils/constants"
import { getValidMaxMinorVersion } from "../../../utils/deploymentDetailsHelpers"
import { extractPageFromBackEndPaginationLink, getEnvVar, isInputValueValid } from "../../../utils/genericHelpers"
import {
  compareTwoRulesList,
  convertComplexConditionToComponentsGroupConditions,
  reorderList,
} from "../../../utils/rulesetHelpers"
import { updateComponentsDataFilesIds } from "../../ImportFlow/Helpers"
import { COMPONENT_TYPES, type SelectedComponent } from "../../Predictions/Interfaces"
import { ComponentTestingDialog } from "../../Predictions/components/ComponentTestingDialog"
import { useWorkflowFeatures } from "../../Workflows/utils/workflowHelpers"
import type { GroupConditions, RuleCardFormikValues, RulesetProps } from "../interfaces"
import { RuleCard } from "./RuleCard"

import styles from "./RulesetCard.module.scss"

// Yup validation schema

// eslint-disable-next-line react-refresh/only-export-components
export const groupSchema: Yup.AnyObjectSchema = Yup.object().shape({
  andOr: Yup.string().required(),
  conditions: Yup.array().of(levelSchema),
  groups: Yup.array().of(Yup.lazy(() => groupSchema) as unknown as Yup.AnyObjectSchema),
})

// eslint-disable-next-line react-refresh/only-export-components
export const validationSchema = Yup.object({
  name: Yup.string().required(t("RulesetCard.validation.rulesetNameRequired", { ns: "rulesets" })),
  cards: Yup.array().of(
    Yup.object().shape({
      name: Yup.string().required(t("RulesetCard.validation.ruleNameRequired", { ns: "rulesets" })),
      conditions: Yup.array().of(levelSchema),
      groups: Yup.array().of(groupSchema),
      label: Yup.string().required(t("RulesetCard.validation.returnLabelRequired", { ns: "rulesets" })),
      label_id: Yup.string().required(t("RulesetCard.validation.chooseFromDropdown", { ns: "rulesets" })),
    }),
  ),
})

export function RulesetLoadingComponent(): ReactElement {
  return (
    <Grid item xs={12}>
      <Card className={styles.cardHeader}>
        <CardHeader className={styles.padding} title={<Skeleton width={200} />} />
        <CardContent className={styles.cardContent}>
          <Skeleton height={30} />
          <Skeleton height={30} />
        </CardContent>
      </Card>
    </Grid>
  )
}

/**
 * Ruleset card component
 * @param {string} uuid
 * @param {boolean} isDuplicate indicate if the ruleset is a duplicate
 * @param {function} closeCreateCard close the create card
 * @param {function} duplicateRuleset duplicate a ruleset
 * @return {React.ReactElement}
 */
export function RulesetCard(props: Readonly<RulesetProps>): React.ReactElement {
  const {
    uuid,
    closeCreateCard,
    duplicateRuleset,
    isDuplicate,
    disableDuplicateButton,
    variant,
    isAllComponentsLoading,
    isPermittedToCloneFiles,
    removeComponent,
    setCompletedCardsCounter,
    setTotalNumOfCurrentProjComponents,
    setComponentsErrorMsgs,
    allComponentsDataRef,
    clonedFilesIdsMap,
    isAudit = false,
    canShowDetails = false,
    metaData,
  } = props

  //hooks
  const { id: projectId } = useParams<{ id: string }>()

  const workflowsList = useSelector((state: RootState) => state?.workflow?.workflows)
  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const { t } = useTranslation("rulesets")

  const canRemoveRuleset = useIsPermitted("Delete ruleset", permissions.konan, flattenedKonanPermissions)
  const canUpdateRuleset = useIsPermitted("Update ruleset", permissions.konan, flattenedKonanPermissions)

  // variable contains all features (schema, computed, constant)
  const features = useWorkflowFeatures(new Set(["SchemaFeatures", "ComputedFeatures", "ConstantFeatures"]))

  const queryClient = useQueryClient()
  const theme = getTheme()

  const { ref: lastVersionRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isRulesetVersionsListLoading && !isFetchingNextPage && fetchNextPage()
    },
  })

  const maxNumberOfRules = Number(getEnvVar("KONAN_MAX_RULE_NUMBER"))

  const isCreateMode = uuid === "new" || !!isDuplicate

  const emptyCondition: Rule = {
    id: uuidv4(),
    feature: "",
    andOr: "and",
    operator: Operators["="],
    valueOrFeature: "Value",
    type: "string",
    value: "",
    secondValue: "",
    secondFeature: "",
    schemaFeatureType: WorkflowSchemaFeatureRequest.type.TEXT,
  }

  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState<boolean>(false)

  const [isEditMode, setIsEditMode] = useState<boolean>(false)
  const [anchor, setAnchor] = React.useState<null | HTMLElement>(null)
  const isMenuOpen = Boolean(anchor)

  const [isVersionDialogOpen, setIsVersionDialogOpen] = useState<boolean>(false)
  const [isTestDialogOpen, setIsTestDialogOpen] = useState<boolean>(false)
  const [selectedVersion, setSelectedVersion] = useState<string | undefined>(undefined)

  // Stores the latest minor version for each major version to determine valid restoration points
  // TODO: Move this state into a separate versioning dialog component along with version related logic
  // for better separation of concerns
  const [maxMinorVersionsMap, setMaxMinorVersionsMap] = useState<Record<string, string>>({})

  // we need this hook to get the mutation loading state from the react query store
  // because the component keeps unmounting and mounting so until we refactor this part we can not depend on the isLoading
  // property on the mutation because when the component unmount and mounts again we lose the isLoading state
  const isMutating = useIsMutating({
    predicate: (mutation) =>
      mutation.options.mutationKey === "filesClone" + uuid ||
      mutation.options.mutationKey === "componentCreate" + uuid ||
      mutation.options.mutationKey === "workFlowCreate" + uuid ||
      mutation.options.mutationKey === "labelsCreate" + uuid,
  })

  // fetch single ruleset
  const { isLoading: isRulesetLoading, data: ruleset } = useQuery<AxiosResponse<RuleSetGroupRetrieve>, AxiosError>(
    ["ruleset", uuid],
    () => KonanAPI.fetchRuleset(projectId as string, uuid),
    {
      onSuccess: async ({ data }) => {
        const activeVersion = data?.active_version

        await formik.setValues({
          uuid: data.uuid,
          name: `${data.name}${isDuplicate ? " - Copy" : ""}`,
          cards:
            activeVersion?.rules?.map((rule) => {
              const { groups, conditions } = convertComplexConditionToComponentsGroupConditions(
                rule?.condition,
                "Ruleset",
              )

              return {
                id: uuidv4(),
                name: rule?.name,
                conditions: conditions && conditions.length > 0 ? conditions : [emptyCondition],
                groups: groups && groups.length > 0 ? groups : [],
                label: rule?.return_label.name,
                label_id: rule?.return_label.uuid,
                condition_list_files: [...rule.condition_list_files],
              }
            }) || [],
        })
      },
      enabled: (!!projectId && uuid !== "new" && variant !== "import" && canShowDetails) || isDuplicate,
    },
  )

  const {
    isLoading: isRulesetVersionsListLoading,
    isFetchingNextPage,
    data: rulesetVersionsList,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedRuleSetVersionsListList>, AxiosError, RuleSetVersionsList>(
    ["rulesetVersionsList", uuid],
    ({ pageParam = 1 }) =>
      KonanAPI.listRulesetVersions({
        projectId: projectId as string,
        rulesetId: uuid,
        page_size: 10,
        page: pageParam,
      }),
    {
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce<RuleSetVersionsList[]>(
          (accumulator, page) => accumulator.concat(page.data.results as RuleSetVersionsList[]),
          [],
        ),
      }),
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: !!projectId && !!uuid && isVersionDialogOpen && variant !== "import",
      refetchOnMount: true,
    },
  )

  // fetch Version
  const { data: rulesetVersionData, isLoading: isRulesetVersionDataLoading } = useQuery<
    AxiosResponse<RuleSetVersionRetrieve>,
    AxiosError
  >(
    ["selectedRulesetVersion", uuid, selectedVersion],
    () =>
      KonanAPI.retrieveRulesetVersion({
        projectId: projectId as string,
        rulesetId: uuid,
        version: selectedVersion as string,
      }),
    {
      onSuccess: () => {
        if (
          selectedVersion &&
          !maxMinorVersionsMap[selectedVersion?.split(".")?.[0] as string] &&
          rulesetVersionsList?.pages
        ) {
          const result = getValidMaxMinorVersion(rulesetVersionsList?.pages, selectedVersion)
          setMaxMinorVersionsMap((prevResults) => ({
            ...prevResults,
            ...result,
          }))
        }
      },
      enabled: !!projectId && !!uuid && isVersionDialogOpen && !!selectedVersion,
    },
  )

  // create workflow mutation
  const createWorkflowMutation = useMutation<AxiosResponse, AxiosError, CreateWorkflowRequest>(
    KonanAPI.createWorkflow,
    {
      mutationKey: "workFlowCreate" + uuid,
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(["workflows", projectId]),
          queryClient.invalidateQueries(["project", projectId]),
        ])
      },
    },
  )

  // ruleset deletion Mutation
  const deleteRulesetMutation = useMutation<
    AxiosResponse,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosError<any>,
    { project_uuid: string; resource_uuid: string }
  >((props) => KonanAPI.deleteWorkflowResource({ ...props, resource_type: "rulesets" }), {
    onSuccess: async () => {
      NotificationUtils.toast(t("notifications.rulesetDeleted"), { severity: "success" })

      await queryClient.invalidateQueries("rulesets")
    },
    onError: ({ response }) => {
      NotificationUtils.toast(response?.data?.details ?? t("notifications.rulesetDeletedError"), { severity: "error" })
    },
  })

  // create ruleset
  const createRulesetMutation = useMutation<AxiosResponse, AxiosError, CreateRulesetRequest>(KonanAPI.createRuleset, {
    mutationKey: "componentCreate" + uuid,
    onSuccess: async () => {
      await queryClient.invalidateQueries("rulesets")
      await queryClient.invalidateQueries("ruleset")
      NotificationUtils.toast(t("notifications.rulesetCreated"), {
        severity: "success",
      })
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError(error: any) {
      if (variant === "import") {
        const formattedErrorMessage =
          capitalize(error?.response?.data?.name?.[0]) || `something went wrong while creating Ruleset`

        setComponentsErrorMsgs?.((prev) => ({ ...prev, [uuid]: formattedErrorMessage }))
        return
      }

      if (error?.response?.data?.name) {
        NotificationUtils.toast(error?.response?.data?.name[0], {
          severity: "error",
        })
      } else if (error?.response?.data?.condition) {
        NotificationUtils.toast(error?.response?.data?.condition[0], {
          severity: "error",
        })
      } else if (error?.response?.data?.details) {
        NotificationUtils.toast(error?.response?.data?.details, {
          severity: "error",
        })
      } else {
        NotificationUtils.toast(t("notifications.rulesetCreateError"), {
          severity: "error",
        })
      }
    },
  })

  // update ruleset
  const updateRulesetMutation = useMutation<AxiosResponse, AxiosError, UpdateRulesetRequest>(KonanAPI.updateRuleset, {
    onSuccess: async () => {
      await queryClient.invalidateQueries("rulesets")
      await queryClient.invalidateQueries(["ruleset", uuid])
      await queryClient.invalidateQueries("ruleset")
      setIsEditMode(false)
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError(error: any) {
      if (error?.response?.data?.name) {
        NotificationUtils.toast(error?.response?.data?.name[0], {
          severity: "error",
        })
      } else if (error?.response?.data?.condition) {
        NotificationUtils.toast(error?.response?.data?.condition[0], {
          severity: "error",
        })
      } else if (error?.response?.data?.details) {
        NotificationUtils.toast(error?.response?.data?.details, {
          severity: "error",
        })
      } else {
        NotificationUtils.toast(t("notifications.rulesetUpdateError"), {
          severity: "error",
        })
      }
    },
  })

  // change ruleset version
  const rulesetVersionChangeMutation = useMutation<AxiosResponse, AxiosError, VersionChangeRequest>(
    KonanAPI.changeResourceVersion,
  )

  //create label while importing
  const createLabelsMutation = useMutation<
    AxiosResponse<Label[]>,
    AxiosError,
    {
      project_uuid: string
      labelsNames: { name: string }[]
    }
  >(KonanAPI.BulkCreateLabels, {
    mutationKey: "labelsCreate" + uuid,
    onSuccess: ({ data }) => {
      formik.setValues((prev) => {
        prev.cards.forEach((card) => {
          const label = data.find((label) => label.name.toLowerCase() === card.label.toLowerCase())
          if (!label) return

          card.label = label.name
          card.label_id = label.uuid
        })

        return { ...prev }
      })
    },
    onError: () => {
      NotificationUtils.toast(t("notifications.labelCreateError"), { severity: "error" })
    },
  })

  // Clone data file Mutation
  const cloneDataFilesMutation = useMutation<AxiosResponse<DataFileCloneResponse[]>, AxiosError, CloneDataFilesRequest>(
    KonanAPI.cloneDataFiles,
    {
      mutationKey: "filesClone" + uuid,
      onSuccess: async ({ data }) => {
        if (!clonedFilesIdsMap) return

        data.forEach((file) => {
          clonedFilesIdsMap[file.source.uuid] = file.uuid
        })

        await formik.setValues((prev) => {
          const updatedComponent = updateComponentsDataFilesIds(prev, clonedFilesIdsMap) as RuleCardFormikValues

          return { ...updatedComponent }
        })
      },
    },
  )

  const formikInitialValues = (uuid && allComponentsDataRef?.current[uuid]) || {
    name: "",
    uuid: "",
    cards: [
      {
        id: `new-${uuidv4()}`,
        name: "",
        conditions: [emptyCondition] as Rule[],
        groups: [] as GroupConditions[],
        label: "",
        label_id: "",
        condition_list_files: [] as ConditionListFile[],
      },
    ],
  }

  const formik = useFormik({
    initialValues: formikInitialValues,
    validationSchema: validationSchema,
    onSubmit: async ({ name, cards }) => {
      // check cards names for reserved keywords
      for (const card of cards) {
        if (!isInputValueValid("rule", card.name.trim(), PROHIBITED_REASONS_KEYWORDS)) {
          return
        }
      }
      const { trimmedName, featuresArray, finalComponentFormat } = formatWorkflowComponent({
        name,
        cards,
      })

      if (workflowsList?.length === 0 && featuresArray?.length > 0) {
        await createWorkflowMutation.mutateAsync({
          name: "Untitled-1",
          state: WorkflowCreateRequest.state.DISABLED,
          projectId: projectId as string,
          schema: [...featuresArray],
        })
      }

      if (isCreateMode || variant === "import") {
        // Create a new ruleset
        await createRulesetMutation.mutateAsync({
          projectId: projectId as string,
          name: trimmedName,
          rules: [...finalComponentFormat],
        })

        closeCreateCard?.()
        removeComponent?.(uuid)
        setCompletedCardsCounter?.((prev) => ++prev)
      } else {
        if (
          ruleset?.data.active_version?.rules &&
          compareTwoRulesList(finalComponentFormat, ruleset?.data.active_version?.rules)
        ) {
          await updateRulesetMutation.mutateAsync({
            projectId: projectId as string,
            rulesetId: ruleset?.data.uuid as string,
            name: trimmedName === ruleset?.data?.name ? undefined : trimmedName,
          })
        } else {
          await updateRulesetMutation.mutateAsync({
            projectId: projectId as string,
            rulesetId: ruleset?.data.uuid as string,
            name: trimmedName === ruleset?.data?.name ? undefined : trimmedName,
            rules: [...(finalComponentFormat ?? [])],
          })
        }

        setIsEditMode(false)
        NotificationUtils.toast(t("notifications.rulesetUpdated"), {
          severity: "success",
        })

        await queryClient.invalidateQueries("rulesets")
        await queryClient.invalidateQueries("ruleset")
      }
    },
  })

  // This Effect to sync the formik changes with the components ref
  // to submit changes with Save all button
  useEffect(() => {
    if (!allComponentsDataRef) return

    allComponentsDataRef.current[uuid] = formik.values
  }, [formik.values, uuid, allComponentsDataRef])

  const changeRulesetVersion = async (version: string): Promise<void> => {
    try {
      await rulesetVersionChangeMutation.mutateAsync({
        projectId: projectId as string,
        resourceId: uuid,
        resource: "rulesets",
        version,
      })

      NotificationUtils.toast(
        t("notifications.changeRulesVersion", {
          version: version,
        }),
        {
          severity: "success",
        },
      )
      // Invalidate react-query queries
      await Promise.all([
        queryClient.invalidateQueries(["rulesets", projectId]),
        queryClient.invalidateQueries(["ruleset", uuid]),
        queryClient.invalidateQueries(["rulesetVersionsList", uuid]),
      ])
    } catch {
      NotificationUtils.toast(
        t("notifications.changeRulesVersionError", {
          version: version,
        }),
        {
          severity: "error",
        },
      )
    }
  }

  const deleteRule = async (name: string, closeDialogState: Dispatch<SetStateAction<boolean>>): Promise<void> => {
    const clonedArray = ruleset?.data.active_version?.rules

    try {
      const finalFormattedRules = clonedArray?.map((rule) => {
        return { ...rule, return_label: rule?.return_label?.uuid }
      })
      await updateRulesetMutation.mutateAsync({
        projectId: projectId as string,
        rulesetId: ruleset?.data?.uuid ?? "",
        name: undefined,
        rules: [...(finalFormattedRules?.filter((rule) => rule.name !== name) ?? [])],
      })

      // Invalidate react-query queries
      await Promise.all([
        queryClient.invalidateQueries(["rulesets", projectId]),
        queryClient.invalidateQueries(["ruleset", uuid]),
      ])
      closeDialogState(false)

      NotificationUtils.toast(t("notifications.ruleDeleted"), {
        severity: "success",
      })
    } catch (e) {
      console.warn(e)
    }
  }

  const handleCancelRuleset = (): void => {
    const activeVersion = ruleset?.data?.active_version
    const restoredCards =
      activeVersion?.rules?.map((rule) => {
        const { groups, conditions } = convertComplexConditionToComponentsGroupConditions(rule?.condition, "Ruleset")

        return {
          id: uuidv4(),
          name: rule?.name,
          conditions: conditions && conditions.length > 0 ? conditions : [emptyCondition],
          groups: groups && groups.length > 0 ? groups : [],
          label: rule?.return_label.name,
          label_id: rule?.return_label.uuid,
          condition_list_files: [...rule.condition_list_files],
        }
      }) || []

    formik.setValues({
      ...formik.values,
      name: ruleset?.data?.name ?? formik.values.name,
      cards: restoredCards,
    })

    setIsEditMode(false)
    closeCreateCard?.()
    formik.setErrors({})
  }

  const handleRemoveRule = (ruleIndex: number): boolean => {
    if (isCreateMode || isEditMode) {
      formik.setFieldValue(
        "cards",
        formik.values.cards.filter((_, index) => index !== ruleIndex),
      )
      formik.setFieldError(`cards[${ruleIndex}]`, "")
      return false
    } else {
      return true
    }
  }

  // handle clicking the save button for one ruleset
  const saveImportedRulesetHandler = async (): Promise<void> => {
    if (!clonedFilesIdsMap) return

    setComponentsErrorMsgs?.((prev) => {
      delete prev?.[uuid]
      return { ...prev }
    })

    if (allComponentsDataRef?.current[uuid]?.name.trim() === "") {
      setComponentsErrorMsgs?.((prev) => ({ ...prev, [uuid]: t("rulesetNameRequired") }))
      return
    }

    const cardLabelsNames = new Set<string>()

    formik.values.cards.forEach((card) => {
      cardLabelsNames.add(card.label)
    })

    await createLabelsMutation.mutateAsync({
      project_uuid: projectId as string,
      labelsNames: Array.from(cardLabelsNames).map((name) => ({ name })),
    })

    const cardsFilesIds = new Set<string>()

    const clonedFilesIds = Object.values(clonedFilesIdsMap)

    for (const card of formik.values.cards) {
      if (card.condition_list_files.length > 0) {
        card.condition_list_files.forEach((file) => {
          if (file.uuid && !clonedFilesIds.includes(file.uuid))
            cardsFilesIds.add(JSON.stringify({ uuid: file.uuid, version: file.version }))
        })
      }
    }

    if (cardsFilesIds.size > 0) {
      await cloneDataFilesMutation.mutateAsync({
        project_uuid: projectId as string,
        data_files: Array.from(cardsFilesIds).map((file) => JSON.parse(file)),
      })
    }

    await formik.submitForm()
  }

  // helper function for the versioning dialog
  // returns data of the version selected
  const GetRulesetContainer = (): React.ReactElement => {
    const version = rulesetVersionData?.data

    // extracting the current major version
    const majorSelectedVersion = (selectedVersion ?? ruleset?.data.active_version?.version)?.split(".")?.[0]

    // show restore button when the current major version from the selected version/active version existed in our valid versions hashMap
    // and it's not the current active version
    const shouldShowRestoreButton =
      maxMinorVersionsMap[majorSelectedVersion as string] ===
        (selectedVersion ?? ruleset?.data.active_version?.version) &&
      maxMinorVersionsMap[majorSelectedVersion as string] !== ruleset?.data.active_version?.version

    const versionFormik = useFormik({
      initialValues: {
        name: formik.values.name,
        cards: version?.rules?.map((rule) => {
          const { groups, conditions } = convertComplexConditionToComponentsGroupConditions(rule?.condition, "Ruleset")

          return {
            name: rule?.name,
            conditions: conditions && conditions.length > 0 ? conditions : [emptyCondition],
            groups: groups && groups.length > 0 ? groups : [],
            label: rule?.return_label.name,
            label_id: rule?.return_label.uuid,
            condition_list_files: [...rule.condition_list_files],
          }
        }),
      },
      onSubmit: () => {
        return
      },
    })

    if (isRulesetVersionsListLoading || isRulesetVersionDataLoading) {
      return (
        <Grid container xs={12} gap={1} direction={"column"}>
          <Grid item className={"versioning-dialog-header"}>
            <Skeleton height={30} width={300} />
          </Grid>

          <Grid item px={2}>
            <RulesetLoadingComponent />
          </Grid>
        </Grid>
      )
    }

    return (
      <Grid container>
        {/* Header */}
        <Grid
          item
          xs={12}
          alignItems="center"
          display="flex"
          justifyContent="space-between"
          className={"versioning-dialog-header"}
        >
          <Grid item gap={1} xs={9} display="flex">
            <Typography variant="h2-bold" style={{ textTransform: "inherit", marginRight: "5px" }}>
              {ruleset?.data.name}
            </Typography>

            <VersionTag
              version={version?.version ?? selectedVersion}
              color={version ? (ruleset?.data.active_version?.version === selectedVersion ? "green" : "blue") : "green"}
            />
          </Grid>

          {shouldShowRestoreButton && (
            <Button
              variant="secondary"
              size="small"
              onClick={() => changeRulesetVersion(selectedVersion as string)}
              disabled={rulesetVersionChangeMutation.isLoading}
              isLoading={rulesetVersionChangeMutation.isLoading}
            >
              {t("restoreBtn")}
            </Button>
          )}
        </Grid>

        {/* Body */}
        <Grid item xs={12} p={1.5} pt={0}>
          <FormikProvider value={versionFormik}>
            {version?.rules && 0 < version?.rules?.length ? (
              version?.rules?.map((rule, index: number) => (
                <Grid container key={rule?.name} justifyContent="flex-start" alignItems="center" spacing={2} pt={1.5}>
                  {/*  Timeline section  */}
                  <Grid item xs={1.75} md={1} lg={0.75} xl={0.5}>
                    <Avatar sx={{ width: 32, height: 32 }} className={styles.avatar}>
                      {index + 1}
                    </Avatar>
                  </Grid>

                  <Grid item xs={10.25} md={11} lg={11.25} xl={11.5}>
                    <RuleCard index={index} andOr={["and", "or"]} key={rule.name} />
                  </Grid>
                </Grid>
              ))
            ) : (
              <Grid
                container
                height={"100%"}
                flexDirection="column"
                justifyContent="flex-start"
                alignItems="center"
                pt={1.5}
              >
                <KonanEmptyState title={t("noRulesInVersionTitle")} subTitle={t("noRulesInVersionSubTitle")} />
              </Grid>
            )}
          </FormikProvider>
        </Grid>
      </Grid>
    )
  }

  const isActionBtnsDisabled = !!isMutating || isAllComponentsLoading

  const selectedRuleset: SelectedComponent = useMemo(
    () => ({
      id: ruleset?.data.uuid ?? "",
      type: COMPONENT_TYPES.RULESET,
      data: metaData as RuleSetList,
    }),
    [ruleset?.data, metaData],
  )

  return (
    <Fragment>
      {/* Testing Dialog */}
      {isTestDialogOpen && (
        <ComponentTestingDialog
          isOpen={isTestDialogOpen}
          handleClose={() => setIsTestDialogOpen(false)}
          selectedComponent={selectedRuleset}
        />
      )}

      {isVersionDialogOpen && ruleset?.data.active_version && (
        <VersioningDialog
          activeVersion={ruleset?.data.active_version}
          selectedVersion={selectedVersion}
          setSelectedVersion={setSelectedVersion}
          isOpen={isVersionDialogOpen}
          versions={rulesetVersionsList?.pages || []}
          onClose={() => setIsVersionDialogOpen(false)}
          infiniteScrollProps={{
            hasNextPage,
            isFetchingNextPage,
            lastVersionRef,
          }}
        >
          <GetRulesetContainer />
        </VersioningDialog>
      )}

      {isDeletionDialogOpen && (
        <BaseSimpleDialog
          open={isDeletionDialogOpen}
          name={formik.values.name}
          onClose={() => setIsDeletionDialogOpen(false)}
          onAccept={() => {
            deleteRulesetMutation.mutateAsync({
              project_uuid: projectId as string,
              resource_uuid: uuid,
            })
          }}
          mode={"ruleset-deletion"}
          isLoading={deleteRulesetMutation.isLoading}
        />
      )}

      <Card className="card-box-shadow">
        <CardHeader
          className={styles.padding}
          disableTypography
          title={
            <Grid container direction="column" item xs={12} spacing={1}>
              <Grid item container xs={12} justifyContent="space-between">
                <Grid item container xs={6} display={"flex"} alignItems={"center"}>
                  {isRulesetLoading && canShowDetails ? (
                    <Skeleton width={200} />
                  ) : isEditMode || isCreateMode || (variant === "import" && !isActionBtnsDisabled) ? (
                    <InputText
                      hideDescription
                      id="name"
                      placeholder={t("inputNamePlaceHolder")}
                      value={formik.values.name}
                      handleChange={formik.handleChange}
                      error={formik.touched.name && Boolean(formik.errors.name) && formik.errors.name}
                      handleBlur={formik.handleBlur}
                      disabled={formik.isSubmitting}
                      fullWidth
                    />
                  ) : (
                    <Typography variant="h3-bold" style={{ textTransform: "inherit" }}>
                      {formik.values.name.length ? formik.values.name : metaData?.name}
                    </Typography>
                  )}
                  {!isCreateMode &&
                    !isEditMode &&
                    (metaData?.version || selectedVersion || ruleset?.data.active_version?.version) && (
                      <Grid item ml={1} display={"flex"} alignItems={"center"}>
                        <VersionTag
                          version={selectedVersion ?? metaData?.version ?? ruleset?.data.active_version?.version}
                          color={selectedVersion ? (selectedVersion === metaData?.version ? "green" : "blue") : "green"}
                        />
                      </Grid>
                    )}
                </Grid>

                {!isCreateMode && !isEditMode && variant !== "import" && !isAudit ? (
                  <Grid container item xs justifyContent={"flex-end"}>
                    <IconButton
                      aria-label="settings"
                      size="small"
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchor(event.currentTarget)}
                    >
                      <MoreHorizIcon htmlColor={theme.palette.base.text[1]} />
                    </IconButton>

                    <Menu
                      key="basic-menu"
                      anchorEl={anchor}
                      open={isMenuOpen}
                      onClose={() => setAnchor(null)}
                      menuMaxContent
                    >
                      <MenuItem
                        onClick={() => {
                          setIsTestDialogOpen(true)
                          setAnchor(null)
                        }}
                      >
                        <Typography className={styles.optionsMenuItem} variant="a" color="base" variantColor={2}>
                          {t("testRuleBtn")}
                        </Typography>
                      </MenuItem>
                      {canShowDetails && canRemoveRuleset && (
                        <MenuItem
                          onClick={() => {
                            setIsDeletionDialogOpen(true)
                            setAnchor(null)
                          }}
                        >
                          <Typography variant="a" color="negative" variantColor={2}>
                            {t("removeRulesetBtn")}
                          </Typography>
                        </MenuItem>
                      )}
                    </Menu>
                  </Grid>
                ) : (
                  variant === "import" && (
                    <Grid item display={"flex"} gap={1} alignItems={"center"}>
                      <Button
                        variant="secondary"
                        size="small"
                        disabled={isActionBtnsDisabled}
                        onClick={() => {
                          removeComponent?.(uuid)
                          setTotalNumOfCurrentProjComponents?.((prev) => --prev)
                        }}
                        tooltipPlacement="top"
                        tooltip={t("excludeComponentTooltip")}
                      >
                        {t("dismissBtn")}
                      </Button>
                      <Button
                        variant="primary"
                        size="small"
                        disabled={isActionBtnsDisabled || !isPermittedToCloneFiles}
                        onClick={saveImportedRulesetHandler}
                        isLoading={isActionBtnsDisabled}
                      >
                        {t("saveBtn")}
                      </Button>
                    </Grid>
                  )
                )}
              </Grid>
              {/* Avatar section */}
              {variant !== "import" && (
                <Grid item container xs={12} sx={{ flexDirection: { xs: "column", md: "row" } }}>
                  <Grid container item spacing={1} xs={6} alignItems="flex-start" className={styles.avatarContainer}>
                    <Grid item sx={{ marginTop: "2px" }}>
                      {/* SvgIcon is used to fix square avatars on safari */}
                      <SvgIcon className={styles.modelAvatar}>
                        <KonanAvatar
                          size={24}
                          name={metaData?.created_by ?? ruleset?.data.active_version?.created_by}
                        />
                      </SvgIcon>
                    </Grid>
                    <Grid container item xs={10}>
                      <Grid item>
                        <Typography
                          variant="label-bold"
                          noWrap
                          style={{ width: "fit-content", marginTop: isCreateMode ? "5px" : "0px" }}
                        >
                          {metaData?.created_by ?? ruleset?.data.active_version?.created_by}
                        </Typography>

                        {(metaData?.created_at || ruleset?.data.active_version?.created_by) && !isDuplicate && (
                          <Typography variant="label-regular" noWrap style={{ width: "fit-content" }}>
                            <Tooltip
                              title={moment(metaData?.created_at ?? ruleset?.data.active_version?.created_by).format(
                                "DD/MM/YYYY, h:mm A",
                              )}
                              placement="right"
                            >
                              <Typography
                                variant="label-regular"
                                className={styles.date}
                                style={{ width: "fit-content" }}
                              >
                                {moment(metaData?.created_at ?? ruleset?.data.active_version?.created_at).fromNow()}
                              </Typography>
                            </Tooltip>
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid
                    container
                    item
                    xs={12}
                    md={6}
                    alignSelf={"flex-end"}
                    sx={{ justifyContent: { xs: "flex-start", md: "flex-end" } }}
                  >
                    {!isCreateMode && !isEditMode && !isAudit && canShowDetails && (
                      <Fragment>
                        <Grid item pr={1}>
                          <Button
                            size="small"
                            variant="secondary"
                            disabled={isRulesetLoading || disableDuplicateButton}
                            onClick={() => {
                              duplicateRuleset?.(uuid)
                            }}
                          >
                            {t("duplicateBtn")}
                          </Button>
                        </Grid>

                        <Grid item pr={1}>
                          <Button
                            size="small"
                            variant="secondary"
                            disabled={isRulesetLoading}
                            onClick={() => {
                              setSelectedVersion(ruleset?.data.active_version?.version)
                              setIsVersionDialogOpen(true)
                            }}
                          >
                            {t("historyBtn")}
                          </Button>
                        </Grid>
                      </Fragment>
                    )}

                    {!isRulesetLoading && !isEditMode && !isCreateMode && canShowDetails && canUpdateRuleset ? (
                      <Grid item alignSelf={"flex-end"}>
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={() => {
                            setIsEditMode(!isEditMode)
                          }}
                        >
                          {t("editBtn")}
                        </Button>
                      </Grid>
                    ) : (
                      !isRulesetLoading &&
                      (isEditMode || isCreateMode) &&
                      variant !== "import" && (
                        <Grid item gap={1} display={"flex"} alignSelf={"flex-end"}>
                          <Grid item>
                            <Button
                              disabled={isRulesetLoading}
                              size="small"
                              onClick={handleCancelRuleset}
                              variant={"secondary"}
                            >
                              {t("cancelBtn")}
                            </Button>
                          </Grid>

                          <Grid item>
                            <Button
                              variant="primary"
                              size="small"
                              disabled={formik.isSubmitting || isRulesetLoading}
                              onClick={formik.submitForm}
                              isLoading={formik.isSubmitting}
                            >
                              {t("saveBtn")}
                            </Button>
                          </Grid>
                        </Grid>
                      )
                    )}
                  </Grid>
                </Grid>
              )}

              {canShowDetails && (
                <Grid item container>
                  <Typography variant="p" display="block" style={{ textTransform: "inherit" }}>
                    {t("rulesetInfo")}
                  </Typography>
                </Grid>
              )}
            </Grid>
          }
        />

        <CardContent className={styles.cardContent}>
          {isRulesetLoading && canShowDetails ? (
            <RulesetLoadingComponent />
          ) : canShowDetails || isCreateMode || isEditMode ? (
            <Fragment>
              <Grid container direction="column" justifyContent="flex-start" alignItems="center" spacing={2}>
                <FormikProvider value={formik}>
                  {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    formik.values.cards.map((rule: any, index) => (
                      <Grid container item key={index} justifyContent="flex-start" alignItems="center" spacing={2}>
                        {/*  Timeline section  */}
                        <Grid item xs={1.75} md={1} lg={0.75} xl={0.5}>
                          <Grid container direction="column" justifyContent="center" alignItems="center" spacing={2}>
                            {index !== 0 && (isEditMode || isCreateMode) && (
                              <Grid item>
                                <Button
                                  variant="primary"
                                  size="small"
                                  onClick={() => {
                                    const adjustedList = reorderList(
                                      formik.values.cards,
                                      rule.name,
                                      index !== 0 ? "up" : "down",
                                    )
                                    formik.setFieldValue("cards", [...adjustedList])
                                  }}
                                  disabled={updateRulesetMutation.isLoading}
                                >
                                  {index !== 0 ? (
                                    <ArrowUpwardIcon fontSize="inherit" />
                                  ) : (
                                    <ArrowDownwardIcon fontSize="inherit" />
                                  )}
                                </Button>
                              </Grid>
                            )}

                            <Grid item>
                              <Avatar sx={{ width: 32, height: 32 }} className={styles.avatar}>
                                {index + 1}
                              </Avatar>
                            </Grid>

                            {index !== formik.values.cards.length - 1 && (isEditMode || isCreateMode) && (
                              <Grid item>
                                <Button
                                  variant="primary"
                                  size="small"
                                  onClick={() => {
                                    const adjustedList = reorderList(formik.values.cards, rule.name, "down")
                                    formik.setFieldValue("cards", [...adjustedList])
                                  }}
                                  disabled={updateRulesetMutation.isLoading}
                                >
                                  <ArrowDownwardIcon fontSize="inherit" />
                                </Button>
                              </Grid>
                            )}
                          </Grid>
                        </Grid>

                        <Grid item xs={10.25} md={11} lg={11.25} xl={11.5}>
                          <RuleCard
                            createMode={isCreateMode || rule?.id?.includes("new")}
                            editMode={isEditMode}
                            andOr={["and", "or"]}
                            key={`${rule.id}_${features.length}`}
                            index={index}
                            handleRemoveRule={
                              variant === "import" ? undefined : (index: number) => handleRemoveRule(index)
                            }
                            handleDelete={(name: string, stateFn: Dispatch<SetStateAction<boolean>>) =>
                              deleteRule(name, stateFn)
                            }
                          />
                        </Grid>
                      </Grid>
                    ))
                  }
                </FormikProvider>
              </Grid>

              <Grid container justifyContent="flex-start" alignItems="center" mt={2}>
                {(isEditMode || isCreateMode) && (
                  <Grid item>
                    <Button
                      size="regular"
                      disabled={formik?.values?.cards?.length >= maxNumberOfRules || formik.isSubmitting}
                      tooltip={formik?.values?.cards?.length >= maxNumberOfRules ? t("maxRulesToolTip") : ""}
                      onClick={() => {
                        formik.setFieldValue("cards", [
                          ...formik.values.cards,
                          {
                            id: `new-${uuidv4()}`,
                            name: "",
                            conditions: [emptyCondition],
                            label: "",
                            label_id: "",
                            condition_list_files: [],
                          },
                        ])
                      }}
                    >
                      {t("addRuleBtn")}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Fragment>
          ) : (
            <Grid
              padding={2}
              border={1}
              borderColor={"var(--base-border-1)"}
              bgcolor={"var(--base-background-1)"}
              borderRadius={1}
            >
              <Typography variant="label-regular" color="base">
                {t("hiddenDuePermission")}
              </Typography>
            </Grid>
          )}
        </CardContent>
      </Card>
    </Fragment>
  )
}
